// admin/src/pages/GatewayMonitoring/components/PromotePrimaryDialog.js
'use strict';

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { setGatewayRole, extractGatewayErrorMessage } from '../gatewayApi';

export default function PromotePrimaryDialog({ open, gateway, currentPrimary, onClose, onSuccess, mode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!gateway) return null;

  const handlePromote = async () => {
    setLoading(true);
    setError('');
    try {
      await setGatewayRole(gateway._id, { role: 'primary' });
      onSuccess && onSuccess();
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose && onClose()}
      maxWidth="sm"
      fullWidth
      data-testid="promote-dialog"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UpgradeIcon color="primary" />
        Promote Gateway to Primary
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" data-testid="promote-error-alert">
              {error}
            </Alert>
          )}
          {(() => {
            const targetLabel = (gateway?.directoryLabel || gateway?.directory?.label || '').trim();
            const currentLabel = (currentPrimary?.directoryLabel || currentPrimary?.directory?.label || '').trim();
            const hasDifferentLabel = Boolean(currentPrimary && targetLabel !== currentLabel);

            return hasDifferentLabel ? (
              <Alert severity="warning" data-testid="promote-different-label-warning">
                This will send production logins to {targetLabel || 'an unlabelled directory'}.
              </Alert>
            ) : null;
          })()}
          <DialogContentText>
            Are you sure you want to promote <strong>{gateway.name}</strong> to the <strong>Primary</strong> role?
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">
            The current Primary becomes a Secondary and takes this gateway's priority.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The target gateway must be active and relay-eligible to assume the primary role.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          data-testid="cancel-promote-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePromote}
          disabled={loading}
          data-testid="confirm-promote-btn"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UpgradeIcon />}
        >
          {loading ? 'Promoting...' : 'Promote to Primary'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
